import { useEffect, useState, useCallback } from "react";
import { Zap, Plus, Pencil, Trash2, Save, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Loader } from "@/components/common";
import Modal, { TextField, SelectField } from "@/components/Modal";
import { PageTitle, Panel } from "./AdminShell";
import { formatFCFA, formatDateTime } from "@/lib/format";
import { toast } from "sonner";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}
function toISO(local) { return local ? new Date(local).toISOString() : ""; }

export default function AdminFlashSales() {
  const [rows, setRows] = useState(null);
  const [products, setProducts] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => {
    const [r, p] = await Promise.all([api.get("/admin/flash-sales"), api.get("/products")]);
    setRows(r.data); setProducts(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loader />;
  const del = async (id) => { if (!window.confirm("Supprimer cette vente flash ?")) return; await api.delete(`/admin/flash-sales/${id}`); toast.success("Supprimée"); load(); };
  const isLive = (r) => { const n = new Date().toISOString(); return r.active && r.start <= n && r.end >= n; };

  return (
    <div>
      <PageTitle action={<button onClick={() => setEdit({})} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg inline-flex items-center gap-1" data-testid="add-flash-btn"><Plus className="w-4 h-4" /> Nouvelle vente flash</button>}>Ventes Flash</PageTitle>
      <div className="space-y-3">
        {rows.length === 0 && <Panel className="p-6 text-sm text-slate-400 text-center">Aucune vente flash programmée. Créez-en une pour animer votre page d'accueil.</Panel>}
        {rows.map((r) => (
          <Panel key={r.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`flash-row-${r.id}`}>
            <div className="w-10 h-10 rounded-lg bg-[#FDF3EE] flex items-center justify-center shrink-0"><Zap className="w-5 h-5 text-[#C85A32]" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-800 flex items-center gap-2">{r.title}
                {isLive(r) ? <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">EN COURS</span>
                  : r.active ? <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Programmée</span>
                    : <span className="text-[10px] font-bold bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">Inactive</span>}
              </p>
              <p className="text-xs text-slate-500">{formatDateTime(r.start)} → {formatDateTime(r.end)} · {(r.items || []).length} produit(s)</p>
            </div>
            <button onClick={() => setEdit(r)} className="p-2 rounded-lg border border-slate-200" data-testid={`flash-edit-${r.id}`}><Pencil className="w-4 h-4 text-slate-600" /></button>
            <button onClick={() => del(r.id)} className="p-2 rounded-lg border border-red-200" data-testid={`flash-del-${r.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
          </Panel>
        ))}
      </div>
      {edit && <FlashForm sale={edit} products={products} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}

function FlashForm({ sale, products, onClose, onDone }) {
  const [f, setF] = useState({
    title: sale.title || "Ventes Flash", active: sale.active !== false,
    start: toLocalInput(sale.start), end: toLocalInput(sale.end),
    items: (sale.items || []).map((it) => ({ ...it })),
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const addItem = () => setF((s) => ({ ...s, items: [...s.items, { product_id: "", flash_price: 0, quantity: 0 }] }));
  const updItem = (i, k, v) => setF((s) => { const items = [...s.items]; items[i] = { ...items[i], [k]: v }; return { ...s, items }; });
  const rmItem = (i) => setF((s) => ({ ...s, items: s.items.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.start || !f.end) return toast.error("Renseignez le début et la fin");
    const payload = {
      title: f.title, active: f.active, start: toISO(f.start), end: toISO(f.end),
      items: f.items.filter((it) => it.product_id).map((it) => ({ product_id: it.product_id, flash_price: Number(it.flash_price), quantity: Number(it.quantity) || 0 })),
    };
    try {
      if (sale.id) await api.put(`/admin/flash-sales/${sale.id}`, payload);
      else await api.post("/admin/flash-sales", payload);
      toast.success("Vente flash enregistrée"); onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal title={sale.id ? "Modifier la vente flash" : "Nouvelle vente flash"} onClose={onClose} testid="flash-form" size="lg">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Titre" value={f.title} onChange={set("title")} testid="ff-title" />
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm font-semibold text-slate-700">Début
            <input type="datetime-local" value={f.start} onChange={(e) => set("start")(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="ff-start" />
          </label>
          <label className="text-sm font-semibold text-slate-700">Fin
            <input type="datetime-local" value={f.end} onChange={(e) => set("end")(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="ff-end" />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" checked={f.active} onChange={(e) => set("active")(e.target.checked)} data-testid="ff-active" /> Active (affichée sur l'accueil pendant sa période)
        </label>
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-slate-700">Produits en promotion flash</span>
            <button type="button" onClick={addItem} className="text-sm font-semibold text-[#C85A32] inline-flex items-center gap-1" data-testid="ff-add-item"><Plus className="w-4 h-4" /> Ajouter</button>
          </div>
          <div className="space-y-2">
            {f.items.length === 0 && <p className="text-xs text-slate-400">Aucun produit. Ajoutez-en au moins un.</p>}
            {f.items.map((it, i) => {
              const prod = products.find((p) => p.id === it.product_id);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-50 rounded-lg p-2" data-testid={`ff-item-${i}`}>
                  <div className="col-span-12 sm:col-span-5">
                    <SelectField label="Produit" value={it.product_id} onChange={(v) => updItem(i, "product_id", v)} testid={`ff-item-prod-${i}`} options={products.map((p) => ({ value: p.id, label: `${p.name} (${formatFCFA(p.price)})` }))} />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <TextField label="Prix flash" type="number" value={it.flash_price} onChange={(v) => updItem(i, "flash_price", v)} testid={`ff-item-price-${i}`} />
                  </div>
                  <div className="col-span-5 sm:col-span-3">
                    <TextField label="Qté (0=illim.)" type="number" value={it.quantity} onChange={(v) => updItem(i, "quantity", v)} testid={`ff-item-qty-${i}`} required={false} />
                  </div>
                  <div className="col-span-1">
                    <button type="button" onClick={() => rmItem(i)} className="p-2 rounded-lg border border-red-200 mb-0.5" data-testid={`ff-item-rm-${i}`}><X className="w-4 h-4 text-red-600" /></button>
                  </div>
                  {prod && Number(it.flash_price) > 0 && Number(it.flash_price) < prod.price && (
                    <p className="col-span-12 text-[11px] text-emerald-600 font-semibold">-{Math.round((1 - it.flash_price / prod.price) * 100)}% vs prix normal ({formatFCFA(prod.price)})</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg inline-flex items-center justify-center gap-2" data-testid="ff-submit"><Save className="w-4 h-4" /> Enregistrer</button>
      </form>
    </Modal>
  );
}
